// Обёртка над fetch: cookie-сессия, JSON, ошибки сервера → исключение с понятным текстом, 401 → экран входа.

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

type Handler = () => void
let unauthorizedHandler: Handler | null = null

/** Кто реагирует на 401 (модуль auth переключает интерфейс на экран входа). */
export function setUnauthorizedHandler(fn: Handler | null): void {
  unauthorizedHandler = fn
}

export interface RequestOptions {
  signal?: AbortSignal
  /** Не считать 401 выходом из сессии (например, неверный пароль при входе). */
  ignore401?: boolean
}

export function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError'
}

// Связь с OMA идёт через прокси зоны; обрывы на секунды (502/503/504, сеть) переживаем повтором.
// Изменяющий запрос повторяется с тем же Idempotency-Key — сервер не выполнит его дважды.
const TRANSIENT = new Set([0, 502, 503, 504])
const RETRY_DELAYS = [700, 1800, 4000, 8000]
export const newKey = (): string => (typeof globalThis.crypto?.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`)

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      clearTimeout(t)
      reject(new DOMException('Aborted', 'AbortError'))
    }, { once: true })
  })
}

export async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
  const key = method === 'GET' ? null : newKey()
  for (let attempt = 0; ; attempt++) {
    try {
      return await requestOnce<T>(method, path, body, opts, key)
    } catch (e) {
      if (isAbort(e) || !(e instanceof ApiError) || !TRANSIENT.has(e.status) || attempt >= RETRY_DELAYS.length) throw e
      await sleep(RETRY_DELAYS[attempt]!, opts.signal)
    }
  }
}

async function requestOnce<T>(method: string, path: string, body: unknown, opts: RequestOptions, key: string | null): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (key) headers['Idempotency-Key'] = key
  let res: Response
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: opts.signal,
    })
  } catch (e) {
    if (isAbort(e)) throw e
    throw new ApiError('Нет связи с сервером OMA — проверьте интернет и попробуйте ещё раз', 0)
  }
  const text = await res.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }
  if (!res.ok) {
    if (res.status === 401 && !opts.ignore401) unauthorizedHandler?.()
    throw new ApiError(errorText(data, res.status), res.status)
  }
  return data as T
}

function errorText(data: unknown, status: number): string {
  if (data && typeof data === 'object' && 'error' in data) {
    const e = (data as { error: unknown }).error
    if (typeof e === 'string' && e) return e
    if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
      return (e as { message: string }).message
    }
  }
  if (typeof data === 'string' && data && data.length < 300 && !data.trimStart().startsWith('<')) return data
  if (status === 401) return 'Нужно войти'
  if (status === 403) return 'Недостаточно прав'
  if (status === 404) return 'Не найдено'
  if (status === 502 || status === 503 || status === 504) return `Нет связи с сервером OMA (${status}): сеть до него прерывалась, повторы не помогли. Ничего не сохранено — попробуйте ещё раз`
  if (status >= 500) return `Ошибка сервера (${status})`
  return `Ошибка запроса (${status})`
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body: unknown = {}, opts?: RequestOptions) => request<T>('POST', path, body, opts),
  patch: <T>(path: string, body: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body: unknown, opts?: RequestOptions) => request<T>('PUT', path, body, opts),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
}

/** Строка запроса из объекта; пустые значения пропускаются. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    sp.set(k, v === true ? '1' : String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message
  if (typeof e === 'string') return e
  return 'Неизвестная ошибка'
}

/** Загрузить файл (вложение задачи) с прогрессом; файл ещё не привязан к задаче — привязка при создании задачи или POST /api/tasks/:id/attachments. */
export async function uploadFile<T>(file: File, onProgress?: (fraction: number) => void): Promise<T> {
  const key = newKey()
  for (let attempt = 0; ; attempt++) {
    try {
      return await uploadOnce<T>(file, key, onProgress)
    } catch (e) {
      if (!(e instanceof ApiError) || !TRANSIENT.has(e.status) || attempt >= RETRY_DELAYS.length) throw e
      await sleep(RETRY_DELAYS[attempt]!)
    }
  }
}

function uploadOnce<T>(file: File, key: string, onProgress?: (fraction: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/uploads')
    xhr.withCredentials = true
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.setRequestHeader('X-File-Name', encodeURIComponent(file.name))
    xhr.setRequestHeader('X-File-Type', file.type || 'application/octet-stream')
    xhr.setRequestHeader('Accept', 'application/json')
    xhr.setRequestHeader('Idempotency-Key', key)
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total)
    }
    xhr.onload = () => {
      let body: { error?: string } | null = null
      try { body = JSON.parse(xhr.responseText) } catch { body = null }
      if (xhr.status >= 200 && xhr.status < 300 && body) return resolve(body as T)
      if (xhr.status === 401) unauthorizedHandler?.()
      const msg = xhr.status === 413 ? 'Файл слишком большой (больше 100 МБ)' : body?.error ?? (TRANSIENT.has(xhr.status) ? `Нет связи с сервером OMA (${xhr.status}) — файл не загружен` : `Ошибка загрузки (${xhr.status})`)
      reject(new ApiError(msg, xhr.status))
    }
    xhr.onerror = () => reject(new ApiError('Нет связи с сервером', 0))
    xhr.send(file)
  })
}
